import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import { Eraser } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  plan_bottom_up_day_pricing_bulk_upsert_path,
  plan_bottom_up_day_pricing_bulk_update_label_path,
  plan_bottom_up_day_pricing_bulk_destroy_path,
} from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import LabelPicker from "./LabelPicker";
import type { PriceLevelInfo } from "./BottomUpDailyGrid";

const PRESET_LABELS = ["Spike Day", "Black Friday", "Flash Sale", "Campaign"];

interface Props {
  planSlug: string;
  channelSlug: string;
  selectedDates: string[];
  levels: PriceLevelInfo[];
  suggestedLabels: string[];
  pricingByDate: Record<string, string>;
  onClearQuantities: (dates: string[]) => void;
  onClose: () => void;
}

// Day-edit toolbar. Rendered in place of the channel-tabs row while dates are
// selected, same height as that row so the grid never shifts: assign a price
// level (+ optional label naming the occasion), clear the pricing, or zero the
// quantities.
export default function PromoDayBar({ planSlug, channelSlug, selectedDates, levels, suggestedLabels, pricingByDate, onClearQuantities, onClose }: Props) {
  const market = useMarket();
  const [label, setLabel] = useState("");

  // New selection starts labelless; also covers the bar being re-entered.
  useEffect(() => {
    if (selectedDates.length === 0) setLabel("");
  }, [selectedDates.length]);

  if (selectedDates.length === 0) return null;

  const sorted = [...selectedDates].sort();
  const lo = sorted[0];
  const hi = sorted[sorted.length - 1];
  const rangeLabel = lo === hi ? lo : `${lo} → ${hi}`;
  const suggestions = suggestedLabels.length > 0 ? suggestedLabels : PRESET_LABELS;
  const unleveled = selectedDates.filter((d) => !pricingByDate[d]).length;

  // Picking (or clearing) a label applies immediately to days that already
  // carry a price level, so the header bands update on the spot. Days without
  // a level have no pricing row to label yet — for those the picked label
  // rides along with the next level click (bulk_upsert sends it too).
  const handlePickLabel = (value: string) => {
    setLabel(value);
    if (unleveled === selectedDates.length) return;
    router.patch(
      plan_bottom_up_day_pricing_bulk_update_label_path(market.slug, planSlug, channelSlug),
      { dates: selectedDates, label: value },
      { preserveScroll: true, preserveState: true },
    );
  };

  const handleApply = (levelId: string) => {
    router.post(
      plan_bottom_up_day_pricing_bulk_upsert_path(market.slug, planSlug, channelSlug),
      { dates: selectedDates, price_level_id: levelId, label: label.trim() },
      { preserveScroll: true, preserveState: true, onSuccess: onClose },
    );
  };

  const handleClearPricing = () => {
    router.delete(plan_bottom_up_day_pricing_bulk_destroy_path(market.slug, planSlug, channelSlug), {
      data: { dates: selectedDates },
      preserveScroll: true,
      preserveState: true,
      onSuccess: onClose,
    });
  };

  return (
    <div
      style={{ background: "var(--accent)", borderColor: "var(--border)" }}
      className="flex items-center gap-3 px-3 py-1 border-b flex-wrap min-h-[37.5px]"
    >
      <span style={{ color: "var(--muted-foreground)" }} className="text-xs shrink-0">
        {selectedDates.length} day{selectedDates.length !== 1 ? "s" : ""}{" "}
        <span style={{ color: "var(--foreground)" }} className="font-medium tabular-nums">
          ({rangeLabel})
        </span>
      </span>

      <div className="flex items-center gap-1.5">
        {levels.map((level) => (
          <Button key={level.id} onClick={() => handleApply(level.id)} variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
            <span
              style={{ backgroundColor: level.color || "var(--muted)" }}
              className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
            />
            {level.name}
          </Button>
        ))}
      </div>

      <LabelPicker
        label={label}
        suggestions={suggestions}
        onPick={handlePickLabel}
        hint={
          unleveled > 0
            ? `${unleveled} selected day${unleveled !== 1 ? "s" : ""} without a price level — label applies there once a level is set`
            : undefined
        }
      />

      <div className="ml-auto flex items-center gap-1.5">
        <Button onClick={handleClearPricing} variant="outline" size="sm" className="h-7 text-xs">
          Clear pricing
        </Button>
        <Button
          onClick={() => onClearQuantities(selectedDates)}
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          title="Set every product's quantity to 0 on the selected days"
        >
          <Eraser className="h-3.5 w-3.5" />
          Clear quantities
        </Button>
        <Button onClick={onClose} variant="ghost" size="sm" className="h-7 text-xs">
          Cancel
        </Button>
      </div>
    </div>
  );
}
